import { z } from 'zod';
import { jsonObjectSchema } from './json.ts';

export const runtimeSessionDeclarationSchema = z.object({
  id: z.string().min(1),
  capabilityKey: z.string().min(1),
  config: jsonObjectSchema.default({}),
  autostart: z.boolean().default(false),
}).strict();

export const runtimeProjectSpecSchema = z.object({
  sessions: z.array(runtimeSessionDeclarationSchema).max(100).default([]),
  seed: z.number().int().default(20260726),
  defaultSpeed: z.number().positive().max(100).default(1),
}).strict().superRefine((runtime, context) => {
  const ids = new Set<string>();
  for (const [index, session] of runtime.sessions.entries()) {
    if (ids.has(session.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sessions', index, 'id'],
        message: `运行会话编号重复：${session.id}`,
      });
    }
    ids.add(session.id);
  }
});

export const runtimeCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('start') }).strict(),
  z.object({ type: z.literal('pause') }).strict(),
  z.object({ type: z.literal('resume') }).strict(),
  z.object({ type: z.literal('stop') }).strict(),
  z.object({ type: z.literal('reset') }).strict(),
  z.object({
    type: z.literal('step'),
    steps: z.number().int().positive().max(10000).default(1),
  }).strict(),
  z.object({
    type: z.literal('speed.set'),
    speed: z.number().positive().max(100),
  }).strict(),
  z.object({
    type: z.literal('parameters.patch'),
    parameters: jsonObjectSchema,
  }).strict(),
  z.object({
    type: z.literal('interaction.dispatch'),
    eventType: z.string().min(1),
    payload: jsonObjectSchema.default({}),
  }).strict(),
]);

export const runtimeStatusSchema = z.enum([
  'idle',
  'starting',
  'running',
  'paused',
  'stopped',
  'completed',
  'error',
  'disposed',
]);

export const runtimeErrorInfoSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  operation: z.string().min(1),
  recoverable: z.boolean(),
  occurredAt: z.string().datetime(),
}).strict();

export const runtimeSessionSnapshotSchema = z.object({
  id: z.string().min(1),
  capabilityKey: z.string().min(1),
  status: runtimeStatusSchema,
  elapsedTime: z.number().nonnegative().finite(),
  speed: z.number().positive().max(100),
  parameters: jsonObjectSchema,
  state: jsonObjectSchema,
  error: runtimeErrorInfoSchema.nullable(),
  execution: z.enum(['main-thread', 'worker', 'remote']).optional(),
}).strict();

export type RuntimeSessionDeclaration = z.infer<
  typeof runtimeSessionDeclarationSchema
>;
export type RuntimeProjectSpec = z.infer<typeof runtimeProjectSpecSchema>;
export type RuntimeCommand = z.infer<typeof runtimeCommandSchema>;
export type RuntimeStatus = z.infer<typeof runtimeStatusSchema>;
export type RuntimeErrorInfo = z.infer<typeof runtimeErrorInfoSchema>;
export type RuntimeSessionSnapshot = z.infer<
  typeof runtimeSessionSnapshotSchema
>;
